/**
 * Hand-entered time sessions — the "New Time Entry" path a supervisor uses when
 * an employee forgot to clock in.
 *
 * Everything here is PURE, so the same rules run in the browser for instant
 * feedback and on the API as the authority. A hand-entered shift writes PAYABLE
 * HOURS directly, with no punch and no photo behind it, so these rules are the
 * only thing standing between a typo and someone's paycheck.
 */
/**
 * Longest single shift a hand-entered session may span.
 *
 * A sanity guard, not a policy: nobody works more than a day straight, so a
 * longer span is a mistyped date (the classic being the right time on the wrong
 * day, which silently pays for a week).
 */
export const MAX_MANUAL_SHIFT_HOURS = 24;
/** Epoch ms for an ISO instant, or `null` when it cannot be parsed. */
function toMs(iso) {
    if (iso === '') {
        return null;
    }
    if (iso === undefined) {
        return null;
    }
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? null : ms;
}
/**
 * Validate a hand-entered time session. Returns EVERY broken rule, not just the
 * first, so the form can show everything to fix in one pass.
 *
 * A hand-entered shift must START in the past — you cannot have already worked
 * hours that have not happened yet.
 *
 * The clock-OUT may run past now (Michael, 2026-09-14: "the system prevents a clock
 * out time of any time later than the current time of when the time entry is being
 * created. I need this restriction removed."). A supervisor entering a shift that is
 * still running, or finishing the paperwork a few minutes before the shift ends, was
 * being blocked by a rule that only ever described the clock-IN.
 */
export function validateManualTimeEntry(input, context) {
    const issues = [];
    if (input.employeeId === '') {
        issues.push({ field: 'employee', message: 'Pick an employee.' });
    }
    else if (!context.employeeIds.has(input.employeeId)) {
        issues.push({
            field: 'employee',
            message: 'You can only add time for the employees you supervise — pick someone from the list.',
        });
    }
    if (input.departmentId === '') {
        issues.push({ field: 'department', message: 'Pick a department.' });
    }
    else if (!context.departmentIds.has(input.departmentId)) {
        issues.push({
            field: 'department',
            message: 'That department is not active — pick one from the list.',
        });
    }
    const inMs = toMs(input.clockInAt);
    const outMs = toMs(input.clockOutAt);
    const nowMs = toMs(context.now);
    if (inMs === null) {
        issues.push({ field: 'clockInAt', message: 'Enter the date and time the shift started.' });
    }
    // A MISSING clock-out is now a legitimate entry — the employee is still on the
    // clock and will finish at the kiosk. A clock-out that is present but unparseable
    // is still an error: the supervisor typed something and it did not make sense.
    if (input.clockOutAt !== undefined && input.clockOutAt !== '' && outMs === null) {
        issues.push({ field: 'clockOutAt', message: 'Enter a valid date and time, or leave it blank to leave the shift open.' });
    }
    if (inMs !== null && outMs !== null) {
        if (outMs <= inMs) {
            issues.push({
                field: 'clockOutAt',
                message: 'The shift must end after it starts — check the end date and time.',
            });
        }
        else {
            const hours = (outMs - inMs) / 3_600_000;
            if (hours > MAX_MANUAL_SHIFT_HOURS) {
                issues.push({
                    field: 'entry',
                    message: `That is a ${hours.toFixed(1)}-hour shift, longer than ` +
                        `${String(MAX_MANUAL_SHIFT_HOURS)} hours — check the DATES, not just the times ` +
                        `(the usual cause is the right time on the wrong day).`,
                });
            }
        }
    }
    // A shift cannot START in the future. The clock-OUT is deliberately unconstrained
    // — see the header.
    if (nowMs !== null) {
        if (inMs !== null && inMs > nowMs) {
            issues.push({
                field: 'clockInAt',
                message: 'Time can only be added for a shift that has already happened.',
            });
        }
    }
    // Overlapping sessions would pay the same hours twice. An OPEN session (no
    // clock-out) is treated as running to the new shift's end, so a manual entry
    // cannot be slipped underneath one that is still running.
    // An entry left OPEN is treated as running to the end of time for this check: it has
    // no end yet, so anything at all after its start overlaps it. Skipping the check for
    // an open entry would let a supervisor clock someone in on top of a shift they are
    // already running, and the one-open-session index would then reject the write with a
    // database error instead of a sentence.
    const effectiveOut = outMs ?? Number.POSITIVE_INFINITY;
    if (inMs !== null && effectiveOut > inMs && input.employeeId !== '') {
        const clash = context.existing.find((row) => {
            if (row.employeeId !== input.employeeId) {
                return false;
            }
            const rowIn = toMs(row.clockInAt);
            if (rowIn === null) {
                return false;
            }
            const rowOut = row.clockOutAt === undefined ? Math.max(effectiveOut, rowIn) : toMs(row.clockOutAt);
            if (rowOut === null) {
                return false;
            }
            return rowIn < effectiveOut && rowOut > inMs;
        });
        if (clash !== undefined) {
            issues.push({
                field: 'entry',
                message: `This employee already has time recorded overlapping that period ` +
                    `(starting ${clash.clockInAt})${clash.clockOutAt === undefined ? ' and still open' : ''} — ` +
                    `edit that session instead of adding a second one.`,
            });
        }
    }
    return issues;
}
//# sourceMappingURL=manual-time-entry.js.map